/**
 * Centralized Data Manager
 *
 * Utilities for exporting/importing all model data from the centralized store.
 * This makes it easy to implement save/load functionality for your software.
 *
 * Usage:
 * - To save: const data = exportAllData(); // Then save to file
 * - To load: importAllData(loadedData); // After loading from file
 */

import { getAllData, clearData } from "./centralized";
import { useModelStore } from "@/store/useModelStore";
import { usePropertyStore } from "@/store/usePropertyStore";
import { useSupportStore } from "@/store/useSupportStore";
import { useUIStore } from "@/store/useUIStore";
import { useProjectStore } from "@/store/useProjectStore";
import { useUnitStore } from "@/store/useUnitStore";
import { useSettingsStore } from "@/store/useSettingsStore";
import { useBeamSectionStore } from "@/hooks/useBeamSection";
import { useBeamTrussStore } from "@/hooks/useBeamTrussManagement";
import { useTrussStore } from "@/hooks/useTrussManagement";
import {
  exportToTerminalFormat,
  importFromTerminalFormat,
} from "./terminalFormatter";

interface Vector3D {
  x: number;
  y: number;
  z: number;
}

interface PredefinedBeamLoad {
  id: string;
  name: string;
  configs: Array<{
    type: "distributed1" | "distributed2" | "concentrated" | "twisted";
    values: any;
  }>;
  category?: string;
  beamIds?: string[];
}

interface PredefinedNodeLoad {
  id: string;
  name: string;
  type: "regular" | "perpendicular";
  rotationValues: Vector3D;
  perpendicularValues: Vector3D;
  nodeIds?: string[];
}

/**
 * Complete model data structure for save/load
 */
export interface ModelData {
  // Core structural data
  nodes: any[];
  beams: any[];

  // Properties and sections
  properties: any[];
  beamSections: Record<string, any>;

  // Supports
  supports: any[];

  // Loads
  nodeMomentLoads: Record<string, Vector3D>;
  nodePerpLoads: Record<string, Vector3D>;

  // Predefined Loadcases
  predefinedBeamLoads: PredefinedBeamLoad[];
  predefinedNodeLoads: PredefinedNodeLoad[];

  beamTrusses?: any[];
  nodeTrusses?: any[];

  // Project metadata
  project: any;
  projectMeta: {
    unsavedChanges: boolean;
    autoSaveInterval: number;
    lastAutoSave: number;
  };

  // Configuration
  units: {
    lengthUnit: string;
    forceUnit: string;
  };
  settings: {
    units: any;
    display: any;
    analysis: any;
    performance: any;
  };

  // Metadata
  version: string;
  exportedAt: string;
  exportedBy: string;
}

/**
 * Export all data - returns both JSON and terminal format
 */
export function exportAllDataWithFormats(): {
  jsonData: ModelData;
  terminalData: string;
} {
  const jsonData = exportAllData(); // Keep your existing function
  const terminalData = exportToTerminalFormat();

  return {
    jsonData,
    terminalData,
  };
}

/**
 * Import data - detects format automatically
 */
export function importAllDataAuto(
  content: string,
  clearExisting = true
): { success: boolean; errors: string[] } {
  // Try to detect if it's JSON or terminal format
  const trimmed = content.trim();

  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    // JSON format
    console.log("  Detected JSON format");
    try {
      const data = JSON.parse(content);
      importAllData(data, clearExisting);
      return { success: true, errors: [] };
    } catch (err: any) {
      return { success: false, errors: [err.message] };
    }
  } else if (
    trimmed.includes("NODES START") ||
    trimmed.includes("BEAMS START")
  ) {
    // Terminal format
    console.log("  Detected terminal format");
    return importFromTerminalFormat(content);
  } else {
    return { success: false, errors: ["Unknown file format"] };
  }
}

/**
 * Export all data from the centralized store
 *
 * @returns Complete model data ready for saving to file
 *
 * @example
 * const modelData = exportAllData();
 * const json = JSON.stringify(modelData, null, 2);
 * // Save json to file
 */
/**
 * Export all data from the centralized store
 */
export function exportAllData(): ModelData {
  const centralData = getAllData();

  // Get loads from Zustand stores (NOT localStorage)
  const beamTrussStore = useBeamTrussStore.getState();
  const trussStore = useTrussStore.getState();

  // Get actual beamTrusses array
  const beamTrusses = Array.isArray(beamTrussStore.beamTrusses)
    ? beamTrussStore.beamTrusses
    : [];

  // Get actual nodeTrusses array
  const nodeTrusses = Array.isArray(trussStore.nodeTrusses)
    ? trussStore.nodeTrusses
    : [];

  // Build predefinedBeamLoads from beamTrusses (group by label)
  const predefinedBeamLoadsMap = new Map<string, PredefinedBeamLoad>();

  beamTrusses.forEach((truss: any) => {
    const label = truss.label || "Unnamed";

    if (!predefinedBeamLoadsMap.has(label)) {
      predefinedBeamLoadsMap.set(label, {
        id: truss.id,
        name: label,
        configs: [
          {
            type: truss.type,
            values: {
              dx: truss.dx,
              dy: truss.dy,
              dz: truss.dz,
              dxLeft: truss.dxLeft,
              dyLeft: truss.dyLeft,
              dzLeft: truss.dzLeft,
              dxRight: truss.dxRight,
              dyRight: truss.dyRight,
              dzRight: truss.dzRight,
              fx: truss.fx,
              fy: truss.fy,
              fz: truss.fz,
              referenceNode: truss.referenceNode,
              percentageFromNode: truss.percentageFromNode,
              torque: truss.torque,
              torqueDirection: truss.torqueDirection,
            },
          },
        ],
        category: truss.category || "general",
        beamIds: [...(truss.beamIds || [])],
      });
    } else {
      // Merge beamIds for same load label
      const existing = predefinedBeamLoadsMap.get(label)!;
      if (truss.beamIds) {
        existing.beamIds = [
          ...new Set([...(existing.beamIds || []), ...truss.beamIds]),
        ];
      }
    }
  });

  const predefinedBeamLoads = Array.from(predefinedBeamLoadsMap.values());

  // Build predefinedNodeLoads from nodeTrusses (group by presetName)
  const predefinedNodeLoadsMap = new Map<string, PredefinedNodeLoad>();

  nodeTrusses.forEach((truss: any) => {
    const presetName = truss.presetName || truss.label || "Unnamed";

    if (!predefinedNodeLoadsMap.has(presetName)) {
      predefinedNodeLoadsMap.set(presetName, {
        id: truss.id,
        name: presetName,
        type: truss.type || "regular",
        rotationValues: {
          x: truss.type === "regular" ? truss.rotationValue || 0 : 0,
          y: 0,
          z: 0,
        },
        perpendicularValues: {
          x: truss.type === "perpendicular" ? truss.rotationValue || 0 : 0,
          y: 0,
          z: 0,
        },
        nodeIds: [...(truss.nodeIds || [])],
      });
    } else {
      // Merge nodeIds for same preset
      const existing = predefinedNodeLoadsMap.get(presetName)!;
      if (truss.nodeIds) {
        existing.nodeIds = [
          ...new Set([...(existing.nodeIds || []), ...truss.nodeIds]),
        ];
      }
    }
  });

  const predefinedNodeLoads = Array.from(predefinedNodeLoadsMap.values());

  const modelData: ModelData = {
    // Core structural data
    nodes: centralData.nodes || [],
    beams: centralData.beams || [],

    // Properties and sections
    properties: centralData.properties || [],
    beamSections: centralData.beamSections || {},

    // Supports
    supports: centralData.supports || [],

    // Loads
    nodeMomentLoads: centralData.nodeMomentLoads || {},
    nodePerpLoads: centralData.nodePerpLoads || {},

    // Predefined Loadcases (from Zustand stores)
    predefinedBeamLoads,
    predefinedNodeLoads,

    // Also save raw beamTrusses and nodeTrusses for complete restoration
    beamTrusses: beamTrusses as any,
    nodeTrusses: nodeTrusses as any,

    // Project metadata
    project: centralData.project || null,
    projectMeta: centralData.projectMeta || {
      unsavedChanges: false,
      autoSaveInterval: 5,
      lastAutoSave: 0,
    },

    // Configuration
    units: centralData.units || {
      lengthUnit: "meter",
      forceUnit: "kiloNewton",
    },
    settings: centralData.settings || {
      units: { length: "m", force: "kN", stress: "MPa", temperature: "C" },
      display: {
        theme: "system",
        fontSize: 14,
        gridSize: 1.0,
        snapTolerance: 0.1,
      },
      analysis: {
        defaultSolver: "sparse",
        convergenceTolerance: 1e-6,
        maxIterations: 100,
      },
      performance: {
        enableHardwareAcceleration: true,
        maxCores: 4,
        memoryLimit: 4096,
      },
    },

    // Metadata
    version: "1.0.0",
    exportedAt: new Date().toISOString(),
    exportedBy: "STAADPro Unit Custom Section",
  };

  console.log("📦 [CentralizedDataManager] Exported model data:", {
    nodes: modelData.nodes.length,
    beams: modelData.beams.length,
    properties: modelData.properties.length,
    beamSections: Object.keys(modelData.beamSections).length,
    supports: modelData.supports.length,
    beamTrusses: beamTrusses.length,
    nodeTrusses: nodeTrusses.length,
    momentLoads: Object.keys(modelData.nodeMomentLoads).length,
    perpLoads: Object.keys(modelData.nodePerpLoads).length,
    predefinedBeamLoads: modelData.predefinedBeamLoads.length,
    predefinedNodeLoads: modelData.predefinedNodeLoads.length,
    units: modelData.units.lengthUnit + "/" + modelData.units.forceUnit,
  });

  return modelData;
}

/**
 * Import data into all stores from a saved model
 *
 * This function updates ALL Zustand stores, which will automatically
 * propagate to the centralized store via subscriptions.
 *
 * @param data - Model data loaded from file
 * @param clearExisting - Whether to clear existing data first (default: true)
 *
 * @example
 * const loadedData = JSON.parse(fileContent);
 * importAllData(loadedData);
 */
/**
 * Import data into all stores from a saved model
 */
export function importAllData(
  data: Partial<ModelData>,
  clearExisting = true
): void {
  console.log("📥 [CentralizedDataManager] Importing model data...");

  if (clearExisting) {
    console.log("🧹 [CentralizedDataManager] Clearing existing data...");
    clearData();
  }

  // Import to ModelStore (nodes and beams)
  if (data.nodes || data.beams) {
    if (data.nodes) {
      useModelStore.setState({
        nodes: data.nodes,
        nextNodeId:
          Math.max(0, ...data.nodes.map((n: any) => parseInt(n.id) || 0)) + 1,
      });
      console.log("  ✅ Imported nodes:", data.nodes.length);
    }

    if (data.beams) {
      useModelStore.setState({
        beams: data.beams,
        nextBeamId:
          Math.max(0, ...data.beams.map((b: any) => parseInt(b.id) || 0)) + 1,
      });
      console.log("  ✅ Imported beams:", data.beams.length);
    }
  }

  // Import to PropertyStore
  if (data.properties) {
    usePropertyStore.setState({
      properties: data.properties,
    });
    console.log("  ✅ Imported properties:", data.properties.length);
  }

  // Import to SupportStore
  if (data.supports) {
    useSupportStore.setState({
      supports: data.supports,
    });
    console.log("  ✅ Imported supports:", data.supports.length);
  }

  // Import to UIStore (loads)
  if (data.nodeMomentLoads || data.nodePerpLoads) {
    const updates: any = {};
    if (data.nodeMomentLoads) {
      updates.nodeMomentLoads = data.nodeMomentLoads;
      console.log(
        "  ✅ Imported moment loads:",
        Object.keys(data.nodeMomentLoads).length
      );
    }

    if (data.nodePerpLoads) {
      updates.nodePerpLoads = data.nodePerpLoads;
      console.log(
        "  ✅ Imported perp loads:",
        Object.keys(data.nodePerpLoads).length
      );
    }

    useUIStore.setState(updates);
  }

  // Import Predefined Loadcases to localStorage
  if (data.predefinedBeamLoads) {
    localStorage.setItem(
      "predefined_loads",
      JSON.stringify(data.predefinedBeamLoads)
    );
    console.log(
      "  ✅ Imported predefined beam loads:",
      data.predefinedBeamLoads.length
    );
  } else {
    localStorage.removeItem("predefined_loads");
  }

  if (data.predefinedNodeLoads) {
    localStorage.setItem(
      "predefined_truss_presets",
      JSON.stringify(data.predefinedNodeLoads)
    );
  } else {
    localStorage.removeItem("predefined_truss_presets");
  }

  // Dispatch events ONCE at the end with proper delay
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
    window.dispatchEvent(new CustomEvent("predefined_truss_presets_updated"));
  }, 200);

  // 🔹 Clear existing trusses before re-applying imported loads
  {
    // Clear beam trusses - beamTrusses is an array
    useBeamTrussStore.setState({ beamTrusses: [] });

    // Clear node trusses - nodeTrusses is an array
    useTrussStore.setState({ nodeTrusses: [] });

    console.log("  🧹 Cleared existing beam and node loads");
  }

  // Re-apply beam loads - USE beamTrusses if available (raw data)
  if (data.beamTrusses && data.beamTrusses.length > 0) {
    console.log("  📥 Restoring beam loads from beamTrusses array...");

    const visibleBeamTrusses = data.beamTrusses.map((truss: any) => ({
      ...truss,
      visible: true, // Force visible after load
    }));

    // Restore beamTrusses directly to the store
    useBeamTrussStore.setState({ beamTrusses: visibleBeamTrusses });
    console.log("  ✅ Restored beam loads:", data.beamTrusses.length);

    // Also update localStorage for UI
    if (data.predefinedBeamLoads && data.predefinedBeamLoads.length > 0) {
      localStorage.setItem(
        "predefined_loads",
        JSON.stringify(data.predefinedBeamLoads)
      );
      console.log("  ✅ Updated predefined beam loads in localStorage");
    }
  } else if (data.predefinedBeamLoads && data.predefinedBeamLoads.length > 0) {
    console.log(
      "  📥 Restoring beam loads from predefinedBeamLoads (fallback)..."
    );

    // Build beamTrusses from predefinedBeamLoads
    const reconstructedTrusses: any[] = [];

    data.predefinedBeamLoads.forEach((load) => {
      if (!load.beamIds || !Array.isArray(load.beamIds)) return;

      load.configs?.forEach((config) => {
        reconstructedTrusses.push({
          id: load.id,
          beamIds: load.beamIds,
          type: config.type,
          label: load.name,
          category: load.category || "general",
          visible: true,
          ...config.values,
        });
      });
    });

    useBeamTrussStore.setState({ beamTrusses: reconstructedTrusses });
    localStorage.setItem(
      "predefined_loads",
      JSON.stringify(data.predefinedBeamLoads)
    );
    console.log("  ✅ Restored beam loads from predefined format");
  }

  // Re-apply node loads - USE nodeTrusses if available (raw data)
  if (data.nodeTrusses && data.nodeTrusses.length > 0) {
    console.log("  📥 Restoring node loads from nodeTrusses array...");

    const visibleNodeTrusses = data.nodeTrusses.map((truss: any) => ({
      ...truss,
      visible: true, // Force visible after load
    }));

    // Restore directly to store
    useTrussStore.setState({ nodeTrusses: visibleNodeTrusses });
    console.log("  ✅ Restored node loads:", data.nodeTrusses.length);

    // Also update localStorage for UI
    if (data.predefinedNodeLoads && data.predefinedNodeLoads.length > 0) {
      localStorage.setItem(
        "predefined_truss_presets",
        JSON.stringify(data.predefinedNodeLoads)
      );
      console.log("  ✅ Updated predefined node loads in localStorage");
    }
  } else if (data.predefinedNodeLoads && data.predefinedNodeLoads.length > 0) {
    console.log(
      "  📥 Restoring node loads from predefinedNodeLoads (fallback)..."
    );

    // Build nodeTrusses from predefinedNodeLoads
    const reconstructedTrusses: any[] = [];

    data.predefinedNodeLoads.forEach((preset) => {
      if (!preset.nodeIds || !Array.isArray(preset.nodeIds)) return;

      preset.nodeIds.forEach((nodeId) => {
        reconstructedTrusses.push({
          id: preset.id,
          nodeIds: [nodeId],
          type: preset.type,
          presetName: preset.name,
          label: preset.name,
          visible: true,
          plane: "Y",
          rotationValue:
            preset.rotationValues.x || preset.perpendicularValues.x || 0,
        });
      });
    });

    useTrussStore.setState({ nodeTrusses: reconstructedTrusses });
    localStorage.setItem(
      "predefined_truss_presets",
      JSON.stringify(data.predefinedNodeLoads)
    );
    console.log("  ✅ Restored node loads from predefined format");
  }

  // Import to ProjectStore
  if (data.project) {
    useProjectStore.setState({
      currentProject: data.project,
    });
  }

  if (data.projectMeta) {
    useProjectStore.setState({
      unsavedChanges: data.projectMeta.unsavedChanges,
      autoSaveInterval: data.projectMeta.autoSaveInterval,
      lastAutoSave: data.projectMeta.lastAutoSave,
    });
  }

  // Import to UnitStore
  if (data.units) {
    useUnitStore.setState({
      lengthUnit: data.units.lengthUnit as any,
      forceUnit: data.units.forceUnit as any,
    });
  }

  // Import to SettingsStore
  if (data.settings) {
    useSettingsStore.setState({
      units: data.settings.units,
      display: data.settings.display,
      analysis: data.settings.analysis,
      performance: data.settings.performance,
    });
  }

  // Import to BeamSectionStore
  if (data.beamSections) {
    useBeamSectionStore.setState({
      sections: data.beamSections,
    });
  }

  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("loads-updated"));
    console.log("  Dispatched loads-updated event for UI re-render");
  }, 100);
}

/**
 * Get a summary of current model data
 *
 * @returns Object with counts of all major data types
 */
export function getModelSummary() {
  const data = getAllData();

  return {
    nodes: (data.nodes || []).length,
    beams: (data.beams || []).length,
    properties: (data.properties || []).length,
    beamSections: Object.keys(data.beamSections || {}).length,
    supports: (data.supports || []).length,
    momentLoads: Object.keys(data.nodeMomentLoads || {}).length,
    perpLoads: Object.keys(data.nodePerpLoads || {}).length,
    projectName: data.project?.name || "No project",
    hasData: (data.nodes?.length || 0) > 0 || (data.beams?.length || 0) > 0,
  };
}

/**
 * Validate imported data structure
 *
 * @param data - Data to validate
 * @returns Validation result with any errors
 */
export function validateModelData(data: any): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check version
  if (!data.version) {
    // errors.push('Missing version information');
    console.warn("No version information found (legacy file format)");
  }

  // Check nodes
  if (data.nodes && !Array.isArray(data.nodes)) {
    errors.push("Invalid nodes format (must be array)");
  }

  // Check beams
  if (data.beams && !Array.isArray(data.beams)) {
    errors.push("Invalid beams format (must be array)");
  }

  // Check properties
  if (data.properties && !Array.isArray(data.properties)) {
    errors.push("Invalid properties format (must be array)");
  }

  // Check supports
  if (data.supports && !Array.isArray(data.supports)) {
    errors.push("Invalid supports format (must be array)");
  }

  // Check loads
  if (data.nodeMomentLoads && typeof data.nodeMomentLoads !== "object") {
    errors.push("Invalid moment loads format (must be object)");
  }

  if (data.nodePerpLoads && typeof data.nodePerpLoads !== "object") {
    errors.push("Invalid perp loads format (must be object)");
  }

  // Check loadcase data
  if (data.predefinedBeamLoads && !Array.isArray(data.predefinedBeamLoads)) {
    errors.push("Invalid predefined beam loads format (must be array)");
  }

  if (data.predefinedNodeLoads && !Array.isArray(data.predefinedNodeLoads)) {
    errors.push("Invalid predefined node loads format (must be array)");
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Clear all model data from stores
 *
 * Warning: This will clear ALL data from all stores!
 */
export function clearAllModelData(): void {
  console.warn("[CentralizedDataManager] Clearing ALL model data...");

  // Clear ModelStore
  useModelStore.setState({
    nodes: [],
    beams: [],
    nextNodeId: 1,
    nextBeamId: 1,
    selectedNodeIds: [],
    selectedBeamIds: [],
  });

  // Clear PropertyStore
  usePropertyStore.setState({
    properties: [],
  });

  // Clear SupportStore
  useSupportStore.getState().clearAllSupports();

  // Clear UIStore loads
  useUIStore.setState({
    nodeMomentLoads: {},
    nodePerpLoads: {},
  });

  // Clear Loadcase data from localStorage
  localStorage.removeItem("predefined_loads");
  localStorage.removeItem("predefined_truss_presets");

  // Clear beam trusses - beamTrusses is an array
  useBeamTrussStore.setState({ beamTrusses: [] });
  console.log("  ✅ Cleared beam trusses");

  // Clear node trusses - nodeTrusses is an array
  useTrussStore.setState({ nodeTrusses: [] });
  console.log("  ✅ Cleared node trusses");

  // 🔹 Clear beam and node trusses
  // {
  //   const beamStore = useBeamTrussStore.getState();
  //   if ((beamStore as any).clearAllBeamTrusses) {
  //     (beamStore as any).clearAllBeamTrusses();
  //   } else {
  //     useBeamTrussStore.setState({ beamTrusses: new Map() as any });
  //   }

  //   const trussStore = useTrussStore.getState();
  //   const { nodeTrusses, removeTruss } = trussStore;
  //   if (nodeTrusses && nodeTrusses.size > 0) {
  //     nodeTrusses.forEach((trusses) =>
  //       trusses.forEach((t) => removeTruss(t.id))
  //     );
  //   }
  // }

  // Notify UI components
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
    window.dispatchEvent(new CustomEvent("predefined_truss_presets_updated"));
  }, 50);

  // Clear centralized store
  clearData();
}

/**
 * Default export with all functions
 */
export default {
  exportAllData,
  importAllData,
  getModelSummary,
  validateModelData,
  clearAllModelData,
};
